import { useTranslation } from 'react-i18next';
import type { ChatConversation } from '@/api/chat/chatApi.types';
import { resolveLastMessagePreviewTranslationKey } from '@/api/chat/chatMapping';
import { Icon } from '@/components/common/Icon';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { buildNameInitials } from '@/lib/nameInitials';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { CHAT_UNREAD_BADGE_MAXIMUM } from '@/constants/chat';
import { cn } from '@/lib/utils';

interface ConversationListProps {
  conversations: ChatConversation[];
  activeConversationId: string | null;
  onSelect: (conversation: ChatConversation) => void;
  isLoading: boolean;
  fallbackCounterpartName: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  page?: number;
  pageSize?: number;
  totalItems?: number;
  onPageChange?: (nextPage: number) => void;
}

function buildUnreadBadgeLabel(unreadCount: number): string {
  return unreadCount > CHAT_UNREAD_BADGE_MAXIMUM
    ? String(CHAT_UNREAD_BADGE_MAXIMUM) + '+'
    : String(unreadCount);
}

export function ConversationList({
  conversations,
  activeConversationId,
  onSelect,
  isLoading,
  fallbackCounterpartName,
  searchValue,
  onSearchChange,
  page,
  pageSize,
  totalItems,
  onPageChange,
}: ConversationListProps) {
  const { t } = useTranslation('chat');
  const hasAppliedSearch = searchValue !== undefined && searchValue.trim().length > 0;
  const showPagination =
    onPageChange !== undefined &&
    page !== undefined &&
    pageSize !== undefined &&
    totalItems !== undefined &&
    totalItems > pageSize;

  return (
    <div className="flex flex-col h-full min-h-0 bg-zoowhite">
      <div className="px-4 py-3 border-b border-zooborder">
        <h2 className="text-sm font-bold text-zooindigo mb-2">{t('chat:list.title')}</h2>
        {onSearchChange === undefined ? null : (
          <div className="relative">
            <Icon
              name="Search"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zoomuted text-sm"
            />
            <Input
              value={searchValue === undefined ? '' : searchValue}
              onChange={function updateSearch(changeEvent) {
                onSearchChange(changeEvent.target.value);
              }}
              placeholder={t('chat:list.searchPlaceholder')}
              className="pl-9"
            />
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading === true ? (
          <div className="py-10 flex justify-center">
            <LoadingSpinner className="w-6 h-6" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm font-semibold text-zooindigo">
              {hasAppliedSearch === true
                ? t('chat:list.noResultsTitle')
                : t('chat:list.emptyTitle')}
            </p>
            <p className="text-xs text-zoomuted mt-1">
              {hasAppliedSearch === true ? t('chat:list.noResultsBody') : t('chat:list.emptyBody')}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-zooborder">
            {conversations.map(function renderConversation(conversation) {
              if (conversation.id === null) {
                return null;
              }
              const displayName =
                conversation.counterpartName === null ||
                conversation.counterpartName.trim().length === 0
                  ? fallbackCounterpartName
                  : conversation.counterpartName;
              const previewTranslationKey = resolveLastMessagePreviewTranslationKey(
                conversation.lastMessagePreview
              );
              const previewText =
                previewTranslationKey !== null
                  ? t(previewTranslationKey)
                  : conversation.lastMessagePreview;
              const isActive = conversation.id === activeConversationId;
              return (
                <li key={conversation.id}>
                  <button
                    type="button"
                    onClick={function select() {
                      onSelect(conversation);
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                      isActive === true
                        ? 'bg-zoovpalelavender'
                        : 'hover:bg-zoovpalelavender hover:bg-opacity-50'
                    )}
                  >
                    <div className="w-10 h-10 rounded-full bg-zooprimary text-zoowhite flex items-center justify-center font-bold text-xs shrink-0">
                      {buildNameInitials(displayName)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-bold text-zooindigo truncate">
                          {displayName}
                        </span>
                        {conversation.lastMessageAt === null ? null : (
                          <span className="text-[11px] text-zoomuted shrink-0">
                            {formatIsoDateTimeToTimestamp(conversation.lastMessageAt)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-0.5">
                        <span className="text-xs text-zoomuted truncate">
                          {previewText === null || previewText.length === 0 ? '' : previewText}
                        </span>
                        {conversation.unreadCount > 0 ? (
                          <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-zooprimary text-zoowhite text-[10px] font-bold shrink-0">
                            {buildUnreadBadgeLabel(conversation.unreadCount)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showPagination === true &&
      page !== undefined &&
      pageSize !== undefined &&
      totalItems !== undefined &&
      onPageChange !== undefined ? (
        <div className="px-4 py-2 border-t border-zooborder">
          <ListPagination
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={onPageChange}
          />
        </div>
      ) : null}
    </div>
  );
}
